import { type ReactNode, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { useAuthStore } from '@/stores/auth';
import {
  ackRules,
  PART_1,
  PART_2,
  PartSection,
  useRulesState,
} from './game-rules-page-parts';

type BodyProps = { acked: boolean; onComplete: () => void };

function RulesBody({ acked, onComplete }: BodyProps): ReactNode {
  const r = useRulesState(acked);
  return (
    <div className="-mx-base -my-lg bg-void">
      <PartSection
        part={PART_1}
        watched={r.w1}
        onWatched={r.markW1}
        onClick={r.advance}
      />
      {r.u2 && (
        <PartSection
          part={PART_2}
          watched={r.w2}
          onWatched={r.markW2}
          onClick={onComplete}
        />
      )}
    </div>
  );
}

export function GameRulesPage(): ReactNode {
  const { user } = useAuth();
  const navigate = useNavigate();
  const refreshAuth = useAuthStore((s) => s.refreshAuth);
  const submittingRef = useRef(false);
  const blocked =
    user?.account_status === 'verifying' &&
    (!user.verification_photo || !user.ig_username);
  if (blocked) return <Navigate to="/team/collectors" replace />;
  const acked = !!user?.rules_acknowledged_at;
  const onComplete = (): void => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    void (async (): Promise<void> => {
      try {
        if (!acked) await ackRules(refreshAuth);
        navigate('/');
      } finally {
        submittingRef.current = false;
      }
    })();
  };
  return <RulesBody acked={acked} onComplete={onComplete} />;
}
